import React, { useState, useEffect, useRef } from 'react';
import { C, R, SHADOW, fmt, TAX, DEL_FEE } from '../tokens.js';
import { Card, Btn, BkHdr, Badge, Stepper, Divider, SHead, Toggle, SPill, ImgOrEmoji } from '../components/ui.jsx';
import { D } from '../mock-data.js';
import { quoteBill } from '../api/orders.js';
import { getRecommendations } from '../api/recommendations.js';
import { useApi } from '../lib/hooks.js';

/* ── Dynamic bill renderer ──────────────────────────────────────
   Loops over backend-provided lineItems so any future surcharge or
   discount the backend introduces appears here without code changes.
   ─────────────────────────────────────────────────────────────── */
const BillCard = ({bill, quoting, error}) => {
  if (!bill && !error) {
    return (
      <Card>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{fontSize:13,fontWeight:700,color:C.textMed}}>Bill details</span>
          <span style={{fontSize:11,fontWeight:700,color:C.textLight,letterSpacing:.2}} className="pulse">CALCULATING…</span>
        </div>
        <div style={{marginTop:14,display:'flex',flexDirection:'column',gap:10}}>
          <div className="skeleton" style={{height:12,width:'72%'}}/>
          <div className="skeleton" style={{height:12,width:'58%'}}/>
          <div className="skeleton" style={{height:12,width:'80%'}}/>
        </div>
      </Card>
    );
  }
  if (!bill && error) {
    return (
      <Card style={{border:`1px solid ${C.error}`,background:C.errorBg,boxShadow:'none'}}>
        <p style={{fontSize:13,fontWeight:700,color:C.error}}>Couldn't fetch latest prices</p>
        <p style={{fontSize:12,color:C.textMed,marginTop:4,lineHeight:1.5}}>Retrying… your cart is safe.</p>
      </Card>
    );
  }
  const rowColor = (kind) => kind === 'discount' ? C.success : C.text;
  const sign = (amt, kind) => {
    const abs = fmt(Math.abs(amt));
    if (kind === 'discount' || amt < 0) return `− ${abs}`;
    return abs;
  };
  return (
    <Card>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <span style={{fontSize:13,fontWeight:700,color:C.textMed,letterSpacing:.1}}>Bill details</span>
        {quoting && (
          <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:10,fontWeight:800,color:C.warning,background:C.warningBg,padding:'3px 9px',borderRadius:R.pill,letterSpacing:.4}}>
            <span className="pulse" style={{width:5,height:5,borderRadius:3,background:C.warning,display:'inline-block'}}/>
            UPDATING
          </span>
        )}
      </div>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
        <span style={{fontSize:13,color:C.textMed}}>Subtotal</span>
        <span style={{fontSize:13,fontWeight:600,color:C.text,fontVariantNumeric:'tabular-nums'}}>{fmt(bill.subtotalPaise)}</span>
      </div>
      {bill.lineItems.map((li, i) => (
        <div key={li.code || i} style={{display:'flex',justifyContent:'space-between',marginBottom:8,alignItems:'flex-start',gap:12}}>
          <div style={{flex:1,minWidth:0}}>
            <span style={{fontSize:13,color:C.textMed}}>{li.label}</span>
            {li.hint && <p style={{fontSize:11,color:C.textLight,marginTop:2,lineHeight:1.4}}>{li.hint}</p>}
          </div>
          <span style={{fontSize:13,fontWeight:600,color:rowColor(li.kind),fontVariantNumeric:'tabular-nums',whiteSpace:'nowrap'}}>{sign(li.amountPaise, li.kind)}</span>
        </div>
      ))}
      <Divider my={12}/>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline'}}>
        <span style={{fontSize:15,fontWeight:800,color:C.text,letterSpacing:-.1}}>Total</span>
        <span style={{fontSize:17,fontWeight:800,color:C.primary,fontVariantNumeric:'tabular-nums',letterSpacing:-.2}}>{fmt(bill.totalPaise)}</span>
      </div>
    </Card>
  );
};

/* ── Cross-sell row ─────────────────────────────────────────────
   Inventory-rule based — same-store top sellers / supplier-tagged
   pairs. No personalisation. Used on checkout + inside the Subscribe
   modal.
   ─────────────────────────────────────────────────────────────── */
export const CrossSellRow = ({title='You might also like', items, onAdd, addLabel='+ Add'}) => {
  if (!items || items.length === 0) return null;
  return (
    <Card>
      <SHead title={title}/>
      <div style={{display:'flex',gap:10,overflowX:'auto',margin:'0 -16px',padding:'2px 16px 4px',scrollSnapType:'x mandatory',WebkitOverflowScrolling:'touch'}}>
        {items.map((it) => (
          <div key={it.id} style={{flexShrink:0,width:138,padding:10,borderRadius:R.lg,border:`1px solid ${C.border}`,background:C.white,scrollSnapAlign:'start'}}>
            <ImgOrEmoji src={it.image} fallback={it.em||'🛒'} size={118} radius={R.md} bg="#e6f7fb" style={{width:'100%',height:74,marginBottom:10}}/>
            <p style={{fontSize:12,fontWeight:700,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.3}}>{it.name}</p>
            {it.variantLabel && <p style={{fontSize:10,color:C.textMed,marginTop:2,lineHeight:1.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{it.variantLabel}</p>}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:10,gap:6}}>
              <span style={{fontSize:12,fontWeight:800,color:C.primary,fontVariantNumeric:'tabular-nums'}}>{fmt(it.pricePaise)}</span>
              <button onClick={()=>onAdd(it)} style={{padding:'6px 10px',borderRadius:R.sm,border:'none',background:C.primary,color:C.white,fontSize:11,fontWeight:800,cursor:'pointer',letterSpacing:.2,minHeight:28,boxShadow:'0 1px 2px rgba(12,192,223,.18)'}}>{addLabel}</button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export const CartScreen = ({cart,setCart,navigate,goBack,addresses}) => {
  if(!cart.items.length) return (
    <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column'}}>
      <BkHdr title="Your Cart" onBack={goBack}/>
      <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:32,textAlign:'center'}}>
        <div style={{width:96,height:96,borderRadius:48,background:'#e6f7fb',display:'flex',alignItems:'center',justifyContent:'center',fontSize:48,marginBottom:20}}>🛒</div>
        <h3 style={{fontSize:18,fontWeight:800,color:C.text,marginBottom:8,letterSpacing:-.2}}>Cart is empty</h3>
        <p style={{fontSize:14,color:C.textMed,lineHeight:1.5}}>Browse suppliers to add items</p>
      </div>
    </div>
  );
  const subtotal=cart.items.reduce((a,i)=>a+i.priceP*i.qty,0);
  const tax=Math.round(subtotal*TAX); const total=subtotal+tax+DEL_FEE;
  const updateQty=(idx,q)=>{if(q<=0){setCart(c=>({...c,items:c.items.filter((_,i)=>i!==idx)}));}else{setCart(c=>({...c,items:c.items.map((i,ix)=>ix===idx?{...i,qty:q}:i)}));}};
  return (
    <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column'}}>
      <BkHdr title="Your Cart" onBack={goBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'16px 16px 12px',display:'flex',flexDirection:'column',gap:14}}>
        <div style={{padding:'12px 14px',background:'#e6f7fb',borderRadius:R.lg,border:`1px solid ${C.primaryLight}`,display:'flex',gap:12,alignItems:'center'}}>
          <div style={{width:36,height:36,borderRadius:R.md,background:C.primary,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>🛒</div>
          <div style={{minWidth:0,flex:1}}>
            <p style={{fontSize:13,fontWeight:700,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>Delivering from {cart.branchName}</p>
            <p style={{fontSize:11,color:C.textMed,marginTop:2,lineHeight:1.4}}>Items from other suppliers start a new cart</p>
          </div>
        </div>
        <Card>
          {cart.items.map((item,idx)=>(
            <div key={idx} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 0',borderBottom:idx<cart.items.length-1?`1px solid ${C.border}`:'none'}}>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize:14,fontWeight:700,color:C.text,lineHeight:1.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{item.name}</p>
                <p style={{fontSize:11,color:C.textMed,marginTop:2}}>{item.variant}</p>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
                <Stepper value={item.qty} onChange={q=>updateQty(idx,q)} min={0}/>
                <span style={{fontSize:13,fontWeight:800,color:C.primary,minWidth:56,textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{fmt(item.priceP*item.qty)}</span>
              </div>
            </div>
          ))}
        </Card>
        <Card>
          <p style={{fontSize:13,fontWeight:700,color:C.textMed,marginBottom:12,letterSpacing:.1}}>Bill preview</p>
          {[['Subtotal',fmt(subtotal)],['Delivery fee',fmt(DEL_FEE)],[`GST (${TAX*100}%)`,fmt(tax)]].map(([l,v],i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
              <span style={{fontSize:13,color:C.textMed}}>{l}</span>
              <span style={{fontSize:13,fontWeight:600,color:C.text,fontVariantNumeric:'tabular-nums'}}>{v}</span>
            </div>
          ))}
          <Divider my={12}/>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline'}}>
            <span style={{fontSize:15,fontWeight:800,color:C.text,letterSpacing:-.1}}>Total</span>
            <span style={{fontSize:17,fontWeight:800,color:C.primary,fontVariantNumeric:'tabular-nums',letterSpacing:-.2}}>{fmt(total)}</span>
          </div>
          <p style={{fontSize:11,color:C.textLight,marginTop:8,lineHeight:1.4}}>Final total computed at checkout — surcharges or discounts may apply.</p>
        </Card>
      </div>
      <div style={{padding:'12px 16px 16px',background:C.white,borderTop:`1px solid ${C.border}`,boxShadow:SHADOW.bottomBar}}>
        <Btn full onClick={()=>navigate('checkout',{total})}>Proceed to checkout</Btn>
      </div>
    </div>
  );
};

export const CheckoutScreen = ({cart,setCart,navigate,goBack,addresses}) => {
  const [selAddr,setSelAddr]=useState(addresses.find(a=>a.isDefault)||addresses[0]);
  const [slot,setSlot]=useState('6–8 AM');
  const [payment,setPayment]=useState('upi');
  const [placing,setPlacing]=useState(false);
  const [stockErr,setStockErr]=useState(null); // {items:[{idx,name,available}]}
  const [bill,setBill]=useState(null);
  const [quoting,setQuoting]=useState(false);
  const [quoteError,setQuoteError]=useState(null);
  const quoteSeq = useRef(0);

  // Debounced re-quote whenever the inputs that affect price change.
  // Stale responses are discarded via quoteSeq so the latest request always wins.
  useEffect(() => {
    if (!cart.items.length) { setBill(null); return; }
    const seq = ++quoteSeq.current;
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const next = await quoteBill({
          branchId: cart.branchId,
          items: cart.items.map((i) => ({ productId: i.productId || i.id, variantLabel: i.variant, qty: i.qty, pricePaise: i.priceP })),
          addressId: selAddr?.id,
          slot,
          paymentMode: payment,
        });
        if (seq === quoteSeq.current) { setBill(next); setQuoteError(null); }
      } catch (err) {
        if (seq === quoteSeq.current) setQuoteError(err);
      } finally {
        if (seq === quoteSeq.current) setQuoting(false);
      }
    }, 250);
    return () => clearTimeout(t);
  // Re-quote when items, address, slot, payment, or branch change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.items, selAddr?.id, slot, payment, cart.branchId]);

  // Cross-sell: same-store inventory rules; only triggered when there's a cart branch.
  const recos = useApi(
    () => getRecommendations({
      context: 'order_checkout',
      branchId: cart.branchId,
      items: cart.items.map((i) => ({ productId: i.productId || i.id })),
      limit: 4,
    }),
    [cart.branchId],
  );
  // Filter to same-branch items so adding can't accidentally cross the single-branch cart invariant.
  const sameBranchRecs = (recos.data || []).filter((r) => r.branchId === cart.branchId);

  const addRecommendation = (rec) => {
    if (!setCart) return;
    setCart((c) => {
      const idx = c.items.findIndex((i) => (i.productId || i.id) === rec.id && i.variant === rec.variantLabel);
      if (idx >= 0) {
        return { ...c, items: c.items.map((it, i) => i === idx ? { ...it, qty: it.qty + 1 } : it) };
      }
      return {
        ...c,
        items: [...c.items, {
          productId: rec.id,
          name: rec.name,
          variant: rec.variantLabel,
          priceP: rec.pricePaise,
          qty: 1,
        }],
      };
    });
  };

  // Simulate the T-301 transactional decrement rejecting OOS items.
  // Demo rule: any line with qty >= 5 is treated as exceeding available stock (server says 4 left).
  const checkStock=()=>{const oos=cart.items.map((it,idx)=>it.qty>=5?{idx,name:it.name,available:4}:null).filter(Boolean);return oos.length?oos:null;};
  const place=()=>{
    setStockErr(null);
    if (!bill) return;
    const oos=checkStock();
    if(oos){setStockErr({items:oos});return;}
    setPlacing(true);
    setTimeout(()=>{if(payment==='upi')navigate('upi',{amount:bill.totalPaise});else navigate('order-confirm',{orderId:'ORD-'+Math.floor(1000+Math.random()*9000)});},600);
  };
  const reduceTo=(idx,qty)=>{if(!setCart)return;setCart(c=>({...c,items:c.items.map((it,i)=>i===idx?{...it,qty}:it)}));setStockErr(null);};
  const removeItem=(idx)=>{if(!setCart)return;setCart(c=>({...c,items:c.items.filter((_,i)=>i!==idx)}));setStockErr(null);};
  return (
    <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column'}}>
      <BkHdr title="Checkout" onBack={goBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'16px 16px 12px',display:'flex',flexDirection:'column',gap:14}}>
        {stockErr&&(
          <div className="slide-up" style={{padding:'14px',background:C.errorBg,borderRadius:R.lg,border:`1px solid ${C.error}`}}>
            <div style={{display:'flex',gap:10,alignItems:'flex-start',marginBottom:10}}>
              <span style={{fontSize:18,lineHeight:1,flexShrink:0,marginTop:1}}>⚠️</span>
              <div style={{minWidth:0,flex:1}}>
                <p style={{fontSize:13,fontWeight:800,color:C.error,marginBottom:2,letterSpacing:-.1}}>Stock changed while you were checking out</p>
                <p style={{fontSize:12,color:C.textMed,lineHeight:1.45}}>Adjust the items below to place this order.</p>
              </div>
            </div>
            {stockErr.items.map(it=>{const item=cart.items[it.idx];if(!item)return null;return (
              <div key={it.idx} style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,padding:'10px 0',borderTop:`1px dashed ${C.error}`}}>
                <div style={{minWidth:0,flex:1}}>
                  <p style={{fontSize:13,fontWeight:700,color:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{it.name}</p>
                  <p style={{fontSize:11,color:C.textMed,marginTop:2}}>You asked for {item.qty} · only {it.available} left</p>
                </div>
                <div style={{display:'flex',gap:6,flexShrink:0}}>
                  <button onClick={()=>reduceTo(it.idx,it.available)} style={{padding:'7px 11px',borderRadius:R.sm,border:`1.5px solid ${C.primary}`,background:C.white,color:C.primary,fontSize:11,fontWeight:800,cursor:'pointer',minHeight:30}}>Reduce to {it.available}</button>
                  <button onClick={()=>removeItem(it.idx)} style={{padding:'7px 11px',borderRadius:R.sm,border:`1.5px solid ${C.error}`,background:C.white,color:C.error,fontSize:11,fontWeight:800,cursor:'pointer',minHeight:30}}>Remove</button>
                </div>
              </div>
            );})}
          </div>
        )}
        <Card>
          <SHead title="Deliver to" action="Change" onAction={()=>{}}/>
          {selAddr&&(
            <div style={{padding:'12px 14px',background:C.bg,borderRadius:R.lg,border:`1px solid ${C.border}`}}>
              <Badge bg="#e6f7fb" color={C.primary} sm>{selAddr.label}</Badge>
              <p style={{fontSize:13,color:C.text,marginTop:6,lineHeight:1.5}}>{selAddr.full}</p>
            </div>
          )}
          {addresses.length>1&&(
            <div style={{display:'flex',gap:8,marginTop:10,overflowX:'auto',paddingBottom:2}}>
              {addresses.map(a=>{const on=selAddr?.id===a.id;return (
                <button key={a.id} onClick={()=>setSelAddr(a)} style={{flexShrink:0,padding:'8px 14px',borderRadius:R.md,border:`1.5px solid ${on?C.primary:C.border}`,background:on?'#e6f7fb':C.white,color:on?C.primary:C.textMed,fontSize:12,fontWeight:700,cursor:'pointer',minHeight:34}}>{a.label}{a.isDefault?' · default':''}</button>
              );})}
            </div>
          )}
        </Card>
        <Card>
          <SHead title="Delivery slot"/>
          <div style={{display:'flex',gap:8}}>
            {['6–8 AM','8–10 AM','10 AM–12 PM'].map(s=>{const on=slot===s;return (
              <button key={s} onClick={()=>setSlot(s)} style={{flex:1,padding:'10px 6px',borderRadius:R.lg,border:`1.5px solid ${on?C.primary:C.border}`,background:on?'#e6f7fb':C.white,color:on?C.primary:C.textMed,fontSize:12,fontWeight:700,cursor:'pointer',textAlign:'center',minHeight:54,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2}}>
                <span style={{fontSize:10,fontWeight:600,opacity:.8}}>Tomorrow</span>
                <span>{s}</span>
              </button>
            );})}
          </div>
        </Card>
        <Card>
          <SHead title="Payment mode"/>
          {[{id:'upi',l:'UPI',sub:'GPay, PhonePe, Paytm'},{id:'cod',l:'Cash on delivery',sub:'Pay when order arrives'}].map((pm,idx)=>{const on=payment===pm.id;return (
            <div key={pm.id} onClick={()=>setPayment(pm.id)} style={{display:'flex',gap:12,alignItems:'center',padding:'12px 14px',borderRadius:R.lg,border:`1.5px solid ${on?C.primary:C.border}`,background:on?'#e6f7fb':C.white,marginBottom:idx===0?8:0,cursor:'pointer',minHeight:56}}>
              <div style={{width:20,height:20,borderRadius:10,border:`2px solid ${on?C.primary:'#d6d3d1'}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,background:C.white,transition:'border .15s'}}>{on&&<div style={{width:10,height:10,borderRadius:5,background:C.primary}}/>}</div>
              <div style={{minWidth:0,flex:1}}>
                <p style={{fontSize:14,fontWeight:700,color:C.text,lineHeight:1.3}}>{pm.l}</p>
                <p style={{fontSize:11,color:C.textMed,marginTop:2,lineHeight:1.4}}>{pm.sub}</p>
              </div>
            </div>
          );})}
        </Card>
        <CrossSellRow items={sameBranchRecs} onAdd={addRecommendation}/>
        <BillCard bill={bill} quoting={quoting} error={quoteError}/>
      </div>
      <div style={{padding:'12px 16px 16px',background:C.white,borderTop:`1px solid ${C.border}`,boxShadow:SHADOW.bottomBar}}>
        <Btn full disabled={placing||!cart.items.length||!bill} onClick={place}>
          {placing
            ? 'Placing order…'
            : !bill
              ? 'Calculating total…'
              : stockErr
                ? `Retry · ${fmt(bill.totalPaise)}`
                : `Place order · ${fmt(bill.totalPaise)}`}
        </Btn>
      </div>
    </div>
  );
};

export const UPIScreen = ({amount,navigate,goBack,onSuccess}) => {
  const [status,setStatus]=useState('waiting');
  const [timer,setTimer]=useState(900);
  const [failReason,setFailReason]=useState('');
  useEffect(()=>{if(status!=='waiting')return;const t=setInterval(()=>setTimer(v=>{if(v<=1){clearInterval(t);setStatus('timeout');return 0;}return v-1;}),1000);return()=>clearInterval(t);},[status]);
  // Demo: Cred app simulates a bank-side decline; others succeed.
  const payViaApp=(appName)=>{setStatus('processing');setTimeout(()=>{
    if(appName==='Cred'){setFailReason('Bank declined the transaction. No money was deducted.');setStatus('bank_failed');}
    else{setStatus('success');}
  },1800);};
  useEffect(()=>{if(status==='success')setTimeout(()=>onSuccess(),1200);},[status]);
  const mins=String(Math.floor(timer/60)).padStart(2,'0'), secs=String(timer%60).padStart(2,'0');
  return (
    <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column',background:'#1c1917'}}>
      <div style={{padding:'12px 20px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <button onClick={goBack} style={{width:38,height:38,borderRadius:12,background:'rgba(255,255,255,.1)',border:'none',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}><svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 13L7 9l4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></button>
        <div style={{textAlign:'center'}}><p style={{fontSize:12,color:'rgba(255,255,255,.5)',fontWeight:600}}>TOTAL AMOUNT</p><p style={{fontSize:28,fontWeight:800,color:C.white}}>{fmt(amount)}</p></div>
        <div style={{width:38}}/>
      </div>
      {status==='success'?(
        <div className="scale-in" style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:32,textAlign:'center'}}>
          <div style={{width:80,height:80,borderRadius:40,background:'#dcfce7',display:'flex',alignItems:'center',justifyContent:'center',fontSize:40,marginBottom:20}}>✅</div>
          <p style={{fontSize:22,fontWeight:800,color:C.white}}>Payment successful!</p>
          <p style={{fontSize:14,color:'rgba(255,255,255,.6)',marginTop:8}}>Confirming your order…</p>
        </div>
      ):status==='timeout'?(
        <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:32,textAlign:'center'}}>
          <div style={{fontSize:64,marginBottom:16}}>⏱</div>
          <p style={{fontSize:20,fontWeight:800,color:C.white,marginBottom:8}}>QR expired</p>
          <p style={{fontSize:14,color:'rgba(255,255,255,.6)',marginBottom:24}}>Payment window timed out. No money was deducted.</p>
          <button onClick={()=>{setStatus('waiting');setTimer(900);}} style={{padding:'12px 28px',borderRadius:12,border:'none',background:C.primary,color:C.white,fontSize:14,fontWeight:700,cursor:'pointer'}}>Try again</button>
        </div>
      ):status==='bank_failed'?(
        <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:32,textAlign:'center'}}>
          <div style={{width:80,height:80,borderRadius:40,background:'rgba(220,38,38,.18)',border:`2px solid ${C.error}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:36,marginBottom:20}}>✕</div>
          <p style={{fontSize:20,fontWeight:800,color:C.white,marginBottom:8}}>Payment failed</p>
          <p style={{fontSize:13,color:'rgba(255,255,255,.65)',marginBottom:24,maxWidth:280,lineHeight:1.55}}>{failReason||'Something went wrong with your bank. No money was deducted.'}</p>
          <button onClick={()=>{setFailReason('');setStatus('waiting');setTimer(900);}} style={{padding:'12px 28px',borderRadius:12,border:'none',background:C.primary,color:C.white,fontSize:14,fontWeight:700,cursor:'pointer',marginBottom:10}}>Try another method</button>
          <button onClick={goBack} style={{background:'none',border:'none',color:'rgba(255,255,255,.7)',fontSize:13,fontWeight:600,cursor:'pointer'}}>Back to checkout</button>
        </div>
      ):status==='processing'?(
        <div className="fade-in" style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:32,textAlign:'center'}}>
          <div className="pulse" style={{width:80,height:80,borderRadius:40,background:'rgba(12,192,223,.2)',border:`3px solid ${C.primary}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:32,marginBottom:20}}>⏳</div>
          <p style={{fontSize:20,fontWeight:800,color:C.white}}>Processing payment…</p>
          <p style={{fontSize:14,color:'rgba(255,255,255,.6)',marginTop:8}}>Do not close this screen</p>
        </div>
      ):(
        <div style={{flex:1,overflowY:'auto',padding:'0 20px 24px',display:'flex',flexDirection:'column',gap:20}}>
          <div style={{background:'rgba(255,255,255,.05)',borderRadius:20,padding:24,display:'flex',flexDirection:'column',alignItems:'center'}}>
            <div style={{width:180,height:180,background:C.white,borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',padding:12}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:3}}>
                {Array(49).fill(0).map((_,i)=><div key={i} style={{width:16,height:16,borderRadius:2,background:`rgba(28,25,23,${Math.random()>.4?0.9:0})`}}/>)}
              </div>
            </div>
            <p style={{fontSize:12,color:'rgba(255,255,255,.5)',marginTop:12}}>Scan with any UPI app · Expires <b style={{color:C.white}}>{mins}:{secs}</b></p>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:12}}><div style={{flex:1,height:1,background:'rgba(255,255,255,.1)'}}/><span style={{fontSize:12,color:'rgba(255,255,255,.4)',fontWeight:600}}>OR PAY VIA APP</span><div style={{flex:1,height:1,background:'rgba(255,255,255,.1)'}}/></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            {[{n:'GPay',c:'#34a853'},{n:'PhonePe',c:'#5f259f'},{n:'Paytm',c:'#00baf2'},{n:'Cred',c:'#1a1a2e'}].map(app=>(
              <button key={app.n} onClick={()=>payViaApp(app.n)} style={{padding:'16px',borderRadius:16,border:'1.5px solid rgba(255,255,255,.15)',background:'rgba(255,255,255,.05)',cursor:'pointer',display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:36,height:36,borderRadius:10,background:app.c,display:'flex',alignItems:'center',justifyContent:'center',color:C.white,fontSize:11,fontWeight:800,flexShrink:0}}>{app.n.slice(0,2)}</div>
                <span style={{fontSize:14,fontWeight:700,color:C.white}}>{app.n}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const OrderConfirmScreen = ({orderId,navigate,resetStack,supplierLock,onOrderConfirmed,setActiveTab}) => {
  // Snapshot supplier-lock presence at mount — onOrderConfirmed clears the
  // App-level lock immediately, but this screen should keep showing the
  // "unlocked" copy + "View orders" CTA for the whole visit.
  const wasLocked = React.useRef(!!supplierLock);
  React.useEffect(() => {
    if (wasLocked.current && typeof onOrderConfirmed === 'function') onOrderConfirmed(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="scale-in" style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:32,background:C.bg,textAlign:'center'}}>
      <div style={{width:100,height:100,borderRadius:50,background:'#dcfce7',display:'flex',alignItems:'center',justifyContent:'center',fontSize:50,marginBottom:24}}>✅</div>
      <h2 style={{fontSize:24,fontWeight:800,color:C.text,marginBottom:8}}>Order confirmed!</h2>
      <p style={{fontSize:14,color:C.textMed,marginBottom:4}}>Order <b style={{color:C.text}}>#{orderId}</b></p>
      <p style={{fontSize:13,color:C.textMed,marginBottom:28}}>Estimated delivery: <b style={{color:C.text}}>Tomorrow 6:30 – 8:00 AM</b></p>
      {wasLocked.current && (
        <div style={{width:'100%',marginBottom:16,padding:'10px 14px',borderRadius:12,background:'#dcfce7',border:'1px solid #86efac'}}>
          <p style={{fontSize:12,fontWeight:800,color:'#15803d'}}>✓ You've unlocked the rest of Rekraft</p>
          <p style={{fontSize:11,color:'#16a34a',marginTop:2,lineHeight:1.4}}>Browse other suppliers, build your routine, or explore deals.</p>
        </div>
      )}
      <Card style={{width:'100%',marginBottom:20,padding:'14px 16px',textAlign:'left'}}>
        {[['Order ID',`#${orderId}`],['From',D.branches.grocery[0].name],['Estimated','Tomorrow, 7:00 AM'],['Payment','UPI — Confirmed']].map(([l,v],i)=>(
          <div key={i} style={{display:'flex',justifyContent:'space-between',marginBottom:i<3?8:0}}><span style={{fontSize:13,color:C.textMed}}>{l}</span><span style={{fontSize:13,fontWeight:700,color:C.text}}>{v}</span></div>
        ))}
      </Card>
      {wasLocked.current ? (
        <Btn full onClick={()=>{resetStack();if(typeof setActiveTab==='function')setActiveTab('orders');}}>View orders →</Btn>
      ) : (
        <Btn full onClick={()=>{resetStack();navigate('order-detail',{order:D.orders[1]});}}>Track order →</Btn>
      )}
      <button onClick={resetStack} style={{marginTop:12,background:'none',border:'none',color:C.primary,fontSize:14,fontWeight:600,cursor:'pointer'}}>Back to home</button>
    </div>
  );
};
